// queries/useUploadCheckpoint.ts
"use client";
import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { ShareFileClient } from "@azure/storage-file-share";
import { fetch } from "libs";
import { POST_UPLOAD_CHECKPOINT, POST_ADD_CHECKPOINT } from "api";
import { UploadCheckpointParams, UploadCheckpointProgressCallback } from "types";

const useUploadCheckpoint = (onProgressUpdate: UploadCheckpointProgressCallback) => {
  const toast = useToast();

  const mutationFn = async ({ file, username }: UploadCheckpointParams) => {
    // Step 1: Call POST_UPLOAD_CHECKPOINT to get SAS URL
    const uploadEndpointResponse = await fetch(POST_UPLOAD_CHECKPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model_username: username,
        file_name: file.name.toLowerCase(),
        style_name: file.name.split("-").pop()?.split(".").shift()?.toLowerCase(),
      }),
    }).then((res) => res);

    // Step 2: Create a ShareFileClient with the SAS URL and upload the file
    const fileClient = new ShareFileClient(uploadEndpointResponse.sas_url);

    await fileClient.uploadData(file, {
      onProgress: (progress) => {
        console.log(`Uploaded ${progress.loadedBytes} bytes`);
        onProgressUpdate(progress); // Update progress using the callback
      },
      concurrency: 4,
      rangeSize: 4 * 1024 * 1024,
    });

    // Step 3: Call POST_ADD_CHECKPOINT after successful upload
    const addCheckpointResponse = await fetch(POST_ADD_CHECKPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        file_name: file.name.toLowerCase(),
        model_username: username,
        style_name: file.name.split("-").pop()?.split(".").shift()?.toLowerCase(),
      }),
    }).then((res) => res);

    return addCheckpointResponse;
  };

  return useMutation({
    mutationFn,
    onError: (error: any) => {
      console.error("Error in useUploadCheckpoint:", error);
      // Display a toast with the error message
      toast({
        title: "Error",
        description: error?.detail || "An unexpected error occurred",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
    onSuccess: (data: any) => {
      console.log("Success in useUploadCheckpoint:", data);
    },
  });
};

export default useUploadCheckpoint;
